import client from "./client.js";
import { API_BASE_URL } from "../consts.js";
import { useAuthStore } from "../store/authStore.js";

// ---------------------------------------------------------------------------
// Conversation CRUD
// ---------------------------------------------------------------------------

export async function fetchConversations(params = {}) {
  const res = await client.get("/ai/conversations", { params });
  return res.data.data;
}

export async function fetchConversation(id) {
  const res = await client.get(`/ai/conversations/${id}`);
  return res.data.data;
}

export async function deleteConversation(id) {
  await client.delete(`/ai/conversations/${id}`);
}

export async function submitFeedback(conversationId, messageId, feedback) {
  const res = await client.post(
    `/ai/conversations/${conversationId}/messages/${messageId}/feedback`,
    { feedback }
  );
  return res.data.data;
}

// ---------------------------------------------------------------------------
// SSE chat stream
// ---------------------------------------------------------------------------

/**
 * Open an SSE connection to the AI chat endpoint.
 *
 * @param {Array}       messages         Conversation history: [{role, content}, ...]
 * @param {string|null} conversationId   Existing conversation UUID, or null to start new
 * @param {Function}    onEvent          Called with each parsed SSE event object
 * @param {Function}    onError          Called on fetch/parse errors
 * @returns {Function} cancel — call to abort the stream
 */
export function streamAiChat(messages, conversationId, onEvent, onError) {
  const controller = new AbortController();

  (async () => {
    try {
      const { accessToken, workspace } = useAuthStore.getState();
      const headers = { "Content-Type": "application/json" };
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
      if (workspace?.id) headers["X-Workspace-Id"] = workspace.id;

      const body = { messages };
      if (conversationId) body.conversation_id = conversationId;

      const response = await fetch(`${API_BASE_URL}/ai/chat`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        onError(data.error || `HTTP ${response.status}`);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(line.slice(6));
            onEvent(event);
          } catch {
            // ignore malformed lines
          }
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        onError(err.message || "Connection error");
      }
    }
  })();

  return () => controller.abort();
}
